import {useMemo} from 'react';

import {type SectionWithRef} from '../../../components/PanelBank/types';
import {type LayedOutPanelWithRef} from '../../../util/panelTypes';
import {usePart, usePartMaybe, useParts} from '../hooks';
import type * as PanelBankSectionConfigTypes from './types';

function assemblePanelBankSectionConfig(
  part: PanelBankSectionConfigTypes.PanelBankSectionConfigNormalized,
  panels: LayedOutPanelWithRef[]
): Omit<SectionWithRef, 'ref'> {
  return {
    ...part,
    panels,
  };
}

export function usePanelBankSectionConfig(
  ref: PanelBankSectionConfigTypes.Ref
): SectionWithRef {
  const part = usePart(ref);
  const panels = useParts(part.panelRefs);
  const panelsWithRefs = useMemo(
    () => panels.map((panel, i) => ({...panel, ref: part.panelRefs[i]})),
    [panels, part.panelRefs]
  );

  return useMemo(
    () => ({
      ...assemblePanelBankSectionConfig(part, panelsWithRefs),
      ref,
    }),
    [ref, part, panelsWithRefs]
  );
}

export function usePanelBankSectionConfigMaybe(
  ref: PanelBankSectionConfigTypes.Ref | undefined
): SectionWithRef | undefined {
  const part = usePartMaybe(ref);
  const panels = useParts(part?.panelRefs ?? []);
  const panelsWithRefs = useMemo(
    () =>
      panels
        .map((panel, i) => {
          const panelRef = part?.panelRefs[i];
          return panelRef ? {...panel, ref: panelRef} : null;
        })
        .filter((panel): panel is LayedOutPanelWithRef => panel !== null),
    [panels, part?.panelRefs]
  );

  return useMemo(
    () =>
      part && ref
        ? {
            ...assemblePanelBankSectionConfig(part, panelsWithRefs),
            ref,
          }
        : undefined,
    [ref, part, panelsWithRefs]
  );
}

export function usePanelBankSectionConfigs(
  refs: PanelBankSectionConfigTypes.Ref[]
): SectionWithRef[] {
  const parts = useParts(refs);
  const flatPanels = useMemo(
    () => parts.flatMap(p => p.panelRefs ?? []),
    [parts]
  );
  const panels = useParts(flatPanels);
  const panelsWithRefs = useMemo(() => {
    return panels.map((panel, index) => ({
      ...panel,
      ref: flatPanels[index],
    }));
  }, [panels, flatPanels]);

  return useMemo(() => {
    const offsets = [0];
    for (const p of parts) {
      offsets.push(offsets[offsets.length - 1] + (p.panelRefs?.length ?? 0));
    }
    return parts.map((p, i) => ({
      ...assemblePanelBankSectionConfig(
        p,
        panelsWithRefs.slice(offsets[i], offsets[i + 1])
      ),
      ref: refs[i],
    }));
  }, [parts, panelsWithRefs, refs]);
}
